import { useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../../components/base';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { UserModel } from '../../../context/models/users/UserModel';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import { UsersActionTypes } from '../../../context/stores/users/IUsersActionsTypes';
import AccountDetail from './account-detail';

type ISYSAccountPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allUsers: UserModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<UserModel>;
	showConfirm: boolean;
	handleReloadData: () => void;
	handleSaveData: (data: UserModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<UserModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Tài khoản quản trị`,
		status: state.users.status,
		allUsers: state.users.allUsers,
		showDetail: state.users.showDetail,
		selectedIds: state.users.selectedIds,
		showConfirm: state.users.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<UsersActionTypes>) => ({
	handleReloadData: () => {
		dispatch(actions.users.getAllRequest());
	},
	handleSaveData: (data: UserModel) => {
		dispatch(actions.users.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.users.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.users.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<UserModel>) => {
		dispatch(actions.users.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.users.showConfirm(isShow));
	},
});

const SYSAccountPage = (props: ISYSAccountPageProps) => {
	const dispatch = useDispatch();
	const { users_group_all, status: userGroupStatus } = useSelector((state: AppState) => state.usersGroup);
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allUsers,
		handleReloadData,
		onChangeSelectedIds,
		handleDeleteData,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadData();
		if (users_group_all.length === 0 || userGroupStatus === eBaseActionStatus.reload) dispatch(actions.usersGroup.getAllRequest());
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadData();
		if (users_group_all.length === 0 || userGroupStatus === eBaseActionStatus.reload) dispatch(actions.usersGroup.getAllRequest());
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<UserModel>[] => {
		return [
			{ accessorKey: 'userName', header: 'Tài khoản', size: 150 },
			{ accessorKey: 'fullName', header: 'Tên đầy đủ', minSize: 150 },
			{ accessorKey: 'email', header: 'Email', size: 150 },
			{ 
				accessorKey: 'userGroupId', 
				header: 'Vai trò', 
				size: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const group = users_group_all.find((g: any) => g.id === val);
					return <>{group ? group.name : val}</>;
				}
			},
			{ 
				accessorKey: 'isActive', 
				header: 'Hoạt động', 
				meta: { align: 'center' }, 
				size: 80,
				cell: ({ getValue }) => {
					const isActive = getValue() as boolean;
					return (
						<span className={isActive ? "text-primary/90 font-medium" : "text-destructive/90 font-medium"}>
							{isActive ? 'Hoạt động' : 'Đã khóa'}
						</span>
					);
				}
			},
			{
				id: 'actions',
				header: 'Thao tác',
				meta: { align: 'center' },
				size: 80,
				cell: ({ row }) => (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [users_group_all]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allUsers}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allUsers.length}
								enableGlobalFilter
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: `Tìm ${titlePage.toLowerCase()}...`
								}}
								enableExport
								exportTitle={titlePage}
								onRefresh={handleReloadData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<AccountDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(SYSAccountPage);
